// Silent anonymous sign-in for guests. HARD RULE 6: no login screen.
import {
  GoogleAuthProvider,
  linkWithPopup,
  onAuthStateChanged,
  signInAnonymously,
  signInWithPopup,
  User,
} from 'firebase/auth';
import { auth } from './firebase';

let ready: Promise<User> | null = null;

export function ensureAnonUser(): Promise<User> {
  if (ready) return ready;
  ready = new Promise((resolve, reject) => {
    onAuthStateChanged(auth, (user) => {
      if (user) return resolve(user);
      signInAnonymously(auth).catch(reject);
    });
  });
  return ready;
}

/**
 * Link the current anonymous guest to a Google account. Keeps the same UID so
 * all their prior uploads stay attributed correctly. If the Google account is
 * already linked to a different Firebase user, fall back to a plain sign-in —
 * the caller loses their anonymous history but at least succeeds.
 */
export async function linkAnonToGoogle(): Promise<User> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  const current = auth.currentUser;
  if (current && current.isAnonymous) {
    try {
      const res = await linkWithPopup(current, provider);
      return res.user;
    } catch (e) {
      const code = (e as { code?: string }).code;
      // 'auth/credential-already-in-use' — this Google account has an existing
      // Firebase user. Fall back to signing in with it.
      if (code === 'auth/credential-already-in-use' || code === 'auth/email-already-in-use') {
        const res = await signInWithPopup(auth, provider);
        return res.user;
      }
      throw e;
    }
  }
  const res = await signInWithPopup(auth, provider);
  return res.user;
}
